import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { sendSupportRequest } from "@/lib/api";
import { listTickets, createTicket, replyTicket } from "@/lib/servicesApi";
import { useSiteSettings } from "@/lib/siteSettings";
import {
  CONSULTING_COPY, SUPPORT_SERVICE_IDS, buildSupportRequestPayload, buildTicketPayload, canReply,
  findingOptions, formatDate, ticketErrorKey, validateSupportRequest, validateTicket,
} from "./consultingModel";

const selectCls = "w-full rounded-md border bg-background px-3 py-2 text-base";

// Paid report only. Rendered by ReportPage when data.consulting exists.
export default function ConsultingTab({ data, lang, token }) {
  const C = CONSULTING_COPY[lang] || CONSULTING_COPY.de;
  const c = data.consulting || {};
  return c.active
    ? <ActiveConsulting C={C} lang={lang} token={token} consulting={c} findings={data.audit_findings} />
    : <InactiveConsulting C={C} token={token} />;
}

// "Destek" / "Support": a no-price, multi-select request for additional
// services (owner request 2026-10-01) — the owner reviews the business in
// detail and sends a personal quote afterwards. No checkout here anymore;
// "consulting_only" (the ticket product above) is just one of the options.
function InactiveConsulting({ C, token }) {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", contact: "", services: [] });
  const [errors, setErrors] = useState({});
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleService = (id) => (checked) =>
    setForm((f) => ({ ...f, services: checked ? [...f.services, id] : f.services.filter((s) => s !== id) }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = validateSupportRequest(form, C.reqErrors);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSending(true);
    try {
      await sendSupportRequest(token, buildSupportRequestPayload(form));
      setSent(true);
      toast.success(C.reqDone);
    } catch {
      toast.error(C.reqFailed);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-2xl border border-primary/30 bg-accent/40 p-5 sm:p-6" data-testid="consulting-offer">
      <h2 className="font-heading text-xl font-bold">{C.offerTitle}</h2>
      <p className="mt-2 text-base">{C.offerIntro}</p>
      {sent ? (
        <div className="mt-4 flex items-center gap-2 text-base"><CheckCircle2 className="h-5 w-5 text-primary" /> {C.reqDone}</div>
      ) : (
        <form onSubmit={submit} className="mt-4 max-w-md space-y-3">
          <div className="space-y-2">
            {SUPPORT_SERVICE_IDS.map((id) => (
              <label key={id} className="flex items-start gap-2 text-base">
                <Checkbox checked={form.services.includes(id)} onCheckedChange={toggleService(id)} data-testid={`support-service-${id}`} />
                <span>{C.services[id]}</span>
              </label>
            ))}
          </div>
          {errors.services && <p className="text-sm text-destructive">{errors.services}</p>}
          <Input placeholder={C.reqName} value={form.name} onChange={set("name")} aria-invalid={!!errors.name} />
          {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
          <Input placeholder={C.reqContact} value={form.contact} onChange={set("contact")} aria-invalid={!!errors.contact} />
          {errors.contact && <p className="text-sm text-destructive">{errors.contact}</p>}
          <Button type="submit" className="w-full" disabled={sending} data-testid="consulting-request">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {C.reqSend}
          </Button>
        </form>
      )}
    </div>
  );
}

function ActiveConsulting({ C, lang, token, consulting, findings }) {
  const s = useSiteSettings();
  const [tickets, setTickets] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [left, setLeft] = useState(consulting.tickets_left);
  const [form, setForm] = useState({ subject: "", finding_code: "", message: "", email: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [replying, setReplying] = useState(null);
  const options = findingOptions(findings);

  const load = useCallback(() => {
    listTickets(token)
      .then((r) => setTickets(r.tickets || []))
      .catch(() => setLoadFailed(true));
  }, [token]);
  useEffect(load, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = validateTicket(form, C.errors);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSending(true);
    try {
      const r = await createTicket(token, buildTicketPayload(form));
      setTickets((t) => [r.ticket, ...(t || [])]);
      if (typeof r.tickets_left === "number") setLeft(r.tickets_left);
      setForm((f) => ({ ...f, subject: "", finding_code: "", message: "" }));
      toast.success(C.sent);
    } catch (err) {
      toast.error(C.failures[ticketErrorKey(err)]);
    } finally {
      setSending(false);
    }
  };

  const sendReply = async (ticket) => {
    const text = (drafts[ticket.id] || "").trim();
    if (!text) return;
    setReplying(ticket.id);
    try {
      const r = await replyTicket(token, ticket.id, text.slice(0, 4000));
      setTickets((list) => list.map((x) => (x.id === ticket.id ? r.ticket : x)));
      setDrafts((d) => ({ ...d, [ticket.id]: "" }));
    } catch (err) {
      toast.error(C.failures[ticketErrorKey(err)]);
    } finally {
      setReplying(null);
    }
  };

  const findingTitle = (code) => (options.find((o) => o.code === code) || {}).title;

  return (
    <div className="space-y-6" data-testid="consulting-active">
      <div className="rounded-2xl border bg-card p-5">
        <h2 className="font-heading text-xl font-bold">{C.title}</h2>
        <p className="mt-2 flex flex-wrap gap-x-4 text-base text-muted-foreground">
          {consulting.ends_at && <span>{C.endsAt(formatDate(consulting.ends_at, lang))}</span>}
          <span className="font-medium text-foreground">{C.left(left)}</span>
        </p>
        <p className="mt-1 text-base text-muted-foreground">{C.replyTime(s.response_hours)}</p>
      </div>

      <form onSubmit={submit} className="space-y-2.5 rounded-2xl border bg-card p-5" noValidate>
        <h3 className="font-heading text-base font-semibold">{C.newTitle}</h3>
        <Input placeholder={C.subject} maxLength={120} value={form.subject} onChange={set("subject")} aria-invalid={!!errors.subject} />
        {errors.subject && <p className="text-sm text-destructive">{errors.subject}</p>}
        {options.length > 0 && (
          <label className="block text-sm text-muted-foreground">
            {C.finding}
            <select className={`${selectCls} mt-1`} value={form.finding_code} onChange={set("finding_code")}>
              <option value="">{C.findingNone}</option>
              {options.map((o) => <option key={o.code} value={o.code}>{o.title}</option>)}
            </select>
          </label>
        )}
        <Textarea placeholder={C.message} rows={4} maxLength={4000} value={form.message} onChange={set("message")} aria-invalid={!!errors.message} />
        {errors.message && <p className="text-sm text-destructive">{errors.message}</p>}
        <Input type="email" placeholder={C.email} maxLength={200} value={form.email} onChange={set("email")} aria-invalid={!!errors.email} />
        {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
        <Button type="submit" disabled={sending || left <= 0}>
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {sending ? C.sending : C.send}
        </Button>
      </form>

      <div>
        <h3 className="font-heading text-base font-semibold">{C.yours}</h3>
        {loadFailed ? <p className="mt-2 text-base text-muted-foreground">{C.loadFailed}</p>
          : tickets === null ? <p className="mt-2 text-base text-muted-foreground">{C.loading}</p>
          : tickets.length === 0 ? <p className="mt-2 text-base text-muted-foreground">{C.none}</p>
          : (
            <ul className="mt-3 space-y-4">
              {tickets.map((t) => (
                <li key={t.id} className="rounded-2xl border bg-card p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{t.subject}</span>
                    <span className="rounded-full bg-secondary px-2 py-0.5 text-xs">{C.status[t.status] || t.status}</span>
                    <span className="ml-auto text-xs text-muted-foreground">{formatDate(t.created_at, lang)}</span>
                  </div>
                  {t.finding_code && findingTitle(t.finding_code) && (
                    <p className="mt-1 text-sm text-muted-foreground">{findingTitle(t.finding_code)}</p>
                  )}
                  <ul className="mt-3 space-y-2">
                    {(t.messages || []).map((m, i) => (
                      <li key={i} className={`rounded-xl p-3 text-base ${m.from === "expert" ? "bg-accent/50" : "bg-secondary/60"}`}>
                        <p className="text-xs font-semibold text-muted-foreground">{m.from === "expert" ? C.expert : C.you} · {formatDate(m.at, lang)}</p>
                        {/* Plain text only, never HTML. */}
                        <p className="mt-1 whitespace-pre-wrap break-words">{m.text}</p>
                      </li>
                    ))}
                  </ul>
                  {canReply(t) ? (
                    <div className="mt-3 space-y-2">
                      <Textarea placeholder={C.reply} rows={2} maxLength={4000} value={drafts[t.id] || ""} onChange={(e) => setDrafts((d) => ({ ...d, [t.id]: e.target.value }))} />
                      <Button size="sm" variant="outline" disabled={replying === t.id || !(drafts[t.id] || "").trim()} onClick={() => sendReply(t)}>
                        {replying === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {C.replySend}
                      </Button>
                    </div>
                  ) : t.status === "closed" ? <p className="mt-3 text-sm text-muted-foreground">{C.closedNote}</p> : null}
                </li>
              ))}
            </ul>
          )}
      </div>
    </div>
  );
}
